import { useState } from 'react';
import { Avatar, Box, Theme, styled } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  TeamResponse,
  useAddAllLearnersToTeam,
  useAddTeamLearner,
  useRemoveAllLearnersToTeam,
  useRemoveTeamLearner,
} from 'api';
import { useTranslation } from 'react-i18next';
import { AvailableLearner } from 'api/teams/teamApiTypes';
import { MINIMUM_SEARCH_CHARACTER_LIMIT_CONTAINS } from 'api/search';
import {
  AddRemoveModalProps,
  TextV2,
  SvgHierarchy8Bold,
} from '@insights-ltd/design-library/components';
import { sortOrgsAlphabetically } from '@insights-ltd/design-library/utils';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import PrivateTag from 'components/PrivateTag/PrivateTag';
import { TransformedLearnerResponse } from './LearnersList/learnerType';

const getTranslations = (
  team: TeamResponse | undefined,
  learnerCount: number,
  filteredLearnerCount: number,
  searchTerm: string,
  isGroupedSearchBelowMinimum: boolean,
  t: any,
  availableLearnerCount: number,
  limitExceeded: boolean,
): AddRemoveModalProps<any>['translations'] => {
  return {
    add: t('ui.event-management.team.add-remove-modal.add'),
    remove: t('ui.event-management.team.add-remove-modal.remove'),
    finish: t('ui.event-management.team.add-remove-modal.finish'),
    title: t('ui.event-management.team.add-remove-modal.title', {
      teamName: team?.name || '',
    }),
    clear: t('ui.event-management.team.add-remove-modal.clear'),
    errorMsg: undefined,
    addAllLoading: t(
      'ui.event.management.team.add-remove-learners.dialog.add-all-loading',
      { teamName: team?.name || '' },
    ),
    addAll: {
      addAllText: t('ui.event-management.group.add-remove.add-all', {
        count: availableLearnerCount || 0,
      }),
      confirmAddAllAction: t(
        'ui.event-management.group.add-remove.add-all.confirm',
      ),
      cancelAddAllAction: t(
        'ui.event-management.group.add-remove.add-all.cancel',
      ),
      confirmationTitle: t(
        'ui.event.management.team.add-remove-learners.dialog.add-all-confirm',
        { count: availableLearnerCount || 0 },
      ),
    },
    removeAll: {
      removeAllText: t('ui.event-management.group.add-remove.remove-all'),
      cancelRemoveAllAction: t(
        'ui.event-management.group.add-remove.removal-all.cancel',
      ),
      confirmRemoveAllAction: t(
        'ui.event-management.group.add-remove.removal-all.confirm',
      ),
      confirmationTitle: t(
        'ui.event.management.team.add-remove-learners.dialog.remove-all-confirm',
        { count: learnerCount },
      ),
    },
    findItem: t(
      'ui.event-management.team.add-remove-modal.find-learners-title',
    ),
    findItemPlaceHolder: t(
      'ui.event-management.team.add-remove-modal.search-learners.placeholder',
    ),
    itemsInGroup: t(
      'ui.event-management.team.add-remove-modal.learners-in-group-title',
    ),
    itemsInGroupPlaceholder: t(
      'ui.event-management.team.add-remove-modal.search-learners-in-team.placeholder',
    ),
    itemsInGroupResults: {
      plural: (count: number) =>
        t(
          'ui.event-management.team.add-remove-modal.team-learner-results-plural',
          { count },
        ),
      singular: (count: number) =>
        t(
          'ui.event-management.team.add-remove-modal.team-learner-results-singular',
          { count },
        ),
      none: () =>
        isGroupedSearchBelowMinimum
          ? t('ui.event-management.list-filter.min-characters', {
              count: MINIMUM_SEARCH_CHARACTER_LIMIT_CONTAINS,
            })
          : t(
              'ui.event-management.team.add-remove-modal.learners-in-group-no-search-result',
            ),
    },
    itemsInGroupLoadingMore: t('ui.event-management.loading'),
    findItemResults: {
      prompt: t('ui.event-management.team.add-remove-modal.search-prompt'),
      plural: () =>
        limitExceeded
          ? t('ui.event-management.learners.result.limit-exceeded.search-term')
          : t('ui.event-management.team.add-remove-modal.results.plural', {
              count: filteredLearnerCount,
            }),
      singular: () =>
        t('ui.event-management.team.add-remove-modal.results.singular', {
          count: filteredLearnerCount,
        }),
      none: () =>
        searchTerm.length < MINIMUM_SEARCH_CHARACTER_LIMIT_CONTAINS
          ? t('ui.event-management.list-filter.min-characters', {
              count: MINIMUM_SEARCH_CHARACTER_LIMIT_CONTAINS,
            })
          : t('ui.event-management.team.add-remove-modal.results.none', {
              searchTerm,
            }),
    },
  };
};

export const useAddRemoveModal = (
  team: TeamResponse | undefined,
  learners: AvailableLearner[],
  refetchTeam: any,
  searchTerm: string,
  isGroupedSearchBelowMinimum: boolean,
  availableLearnerCount: number,
  limitExceeded: boolean,
  checkedItems: Record<string, TransformedLearnerResponse>,
  removeCheckedItems: (ids: string[]) => void,
): Omit<
  AddRemoveModalProps<AvailableLearner>,
  'filterBy' | 'showError' | 'itemsInGroup'
> & {
  toggleAddRemoveModal: () => void;
  isRemovingAll: boolean;
} => {
  const teamId = team?.id ?? '';
  const queryClient = useQueryClient();
  const [isAddRemoveModalOpen, setIsAddRemoveModalOpen] = useState(false);
  const { mutateAsync: addLearner, isPending: isAddingLearner } =
    useAddTeamLearner(queryClient);
  const { mutateAsync: removeLearner, isPending: isRemovingLearner } =
    useRemoveTeamLearner(queryClient, teamId);
  const {
    mutate: addAll,
    isPending: isAddingAll,
    isError: isAddingAllError,
    reset: resetAddAllLearners,
  } = useAddAllLearnersToTeam(queryClient);
  const {
    mutate: removeAll,
    isPending: isRemovingAll,
    isError: isRemovingAllError,
    reset: resetRemoveAllLearners,
  } = useRemoveAllLearnersToTeam(queryClient);

  const { t } = useTranslation();

  const onAdd = (userId: string) => {
    addLearner({ learnerId: userId, teamId }).then(() => refetchTeam());
  };

  const onRemove = async (userId: string) => {
    await removeLearner({ learnerId: userId, teamId });
    const idsToRemove = Object.entries(checkedItems)
      .filter(([, learner]) => learner.learnerId === userId)
      .map(([id]) => id);
    if (idsToRemove.length) removeCheckedItems(idsToRemove);
  };

  const onClose = () => {
    resetAddAllLearners();
    resetRemoveAllLearners();
    setIsAddRemoveModalOpen(false);
  };

  const getErrorMessage = () => {
    if (isAddingAllError) {
      return t('ui.event-management.team.add-remove-learners.error.add-all');
    }
    if (isRemovingAllError) {
      return t('ui.event-management.team.add-remove-learners.error.remove-all');
    }

    return undefined;
  };

  const errorMsg = getErrorMessage();

  const availableItems = learners;

  return {
    onAdd,
    onRemove,
    onClose,
    onRemoveAll: () =>
      removeAll(team?.id || '', {
        onSuccess: () => removeCheckedItems(Object.keys(checkedItems)),
      }),
    onAddAll: () => addAll(team?.id || ''),
    toggleAddRemoveModal: () => setIsAddRemoveModalOpen(!isAddRemoveModalOpen),
    availableItems,
    isOpen: isAddRemoveModalOpen,
    translations: {
      ...getTranslations(
        team,
        team?.learnerCount ?? 0,
        availableItems?.length,
        searchTerm,
        isGroupedSearchBelowMinimum,
        t,
        availableLearnerCount,
        limitExceeded,
      ),
      errorMsg,
    },
    isAddLoading: isAddingAll,
    isRemovingAll,
    isMutating:
      isAddingLearner || isRemovingLearner || isAddingAll || isRemovingAll,
  };
};
const StyledHierarchy8Bold = styled(SvgHierarchy8Bold)(({ theme }) => ({
  width: '1.5rem',
  fill: theme.palette.blue.dark,
}));

const StyledOrgList = styled(TextV2)({
  marginLeft: '.5rem !important',
});

const StyledTeamTitle = styled(TextV2)({
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
});

export const TeamHeader = ({ team }: { team: TeamResponse }) => {
  const { t } = useTranslation();
  const {
    name: teamName,
    organisationContext: { type: teamType } = { type: 'SINGLE' },
    visibility,
  } = team;
  const getOrgsText = () => {
    const organisations = team?.organisationContext?.organisations || [];
    const teamOrgsToDisplay = organisations
      .sort(sortOrgsAlphabetically)
      .map((org, i) => {
        if (i < 2) {
          return org.name;
        }
        return null;
      })
      .filter((value) => value != null);
    const orgslist = `${teamOrgsToDisplay.join(', ')}`;
    const moreText =
      organisations.length > 2
        ? `+${organisations.length - 2} ${t('ui.event-management.team.more')}`
        : '';
    return (
      <>
        <TextV2 variant="bodyBold">{orgslist}</TextV2>
        <TextV2 variant="body1"> {moreText}</TextV2>
      </>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
        }}
      >
        {teamType === 'MULTIPLE' && (
          <Avatar
            data-testid="multi-org-icon"
            sx={(theme) => ({
              backgroundColor: theme.palette.background.paper,
              width: '2.5rem',
              marginRight: '.5rem',
            })}
          >
            <StyledHierarchy8Bold />
          </Avatar>
        )}
        <StyledTeamTitle variant="h2" data-testid="team-title">
          {teamName}
        </StyledTeamTitle>
        {visibility === 'PRIVATE' && teamType === 'SINGLE' && (
          <PrivateTag
            sx={(theme: Theme) => ({
              marginLeft: theme.spacing(spacingSizeMap.XS),
              '> div': {
                padding: `${theme.spacing(spacingSizeMap.XS)} 10px`,
              },
            })}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          mt: '1rem',
        }}
      >
        <TextV2 variant="bodyBold">
          {teamType === 'SINGLE'
            ? t('ui.event-management.organisation.subtitle')
            : t('ui.event-management.organisations.subtitle')}
        </TextV2>
        <StyledOrgList variant="bodyBold" sx={{ marginLeft: '1rem' }}>
          {getOrgsText()}
        </StyledOrgList>
      </Box>
    </>
  );
};
